import { Tab } from '@headlessui/react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { TabPaymentDetails } from './tabs/TabPaymentDetails';
import { TabPaymentHistory } from '../profile/tabs/TabPaymentHistory';
import { TabCoursesHistory } from '../profile/tabs/TabCoursesHistory';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export const TabsPaymentValidation = ({
    isOpen,
    idPayment,
    toggleDrawerFilters,
    updateDataTable,
    onValidatePayment,
    onDeclinePayment,
    onLoadingChange,
    onReceiptEditModeChange,
    onReceiptDateSaveCallbackChange,
    onReceiptDateCancelCallbackChange,
    onReceiptDateLoadingSavingChange,
    loadingReceiptDateSaving,
    isReceiptAuxOpen = false,
    onClearReceiptAux,
    onDetailsTabActiveChange,
}) => {
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [isEditingReceiptDate, setIsEditingReceiptDate] = useState(false);
    const cancelReceiptEditRef = useRef(null);

    const tabsList = [
        {
            label: 'detalles',
            allowView: true,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        },
        {
            label: 'cursos',
            allowView: true,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
            )
        },
        {
            label: 'pagos',
            allowView: true,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
            )
        },
    ];

    // Filter tabs based on allowView
    const visibleTabs = tabsList.filter(tab => tab.allowView);

    // Reset tab index cuando cambia el usuario o se abre el drawer
    useEffect(() => {
        setSelectedTabIndex(0);
    }, [idPayment, isOpen]);

    // Validar que el índice seleccionado sea válido
    useEffect(() => {
        if (selectedTabIndex >= visibleTabs.length) {
            setSelectedTabIndex(0);
        }
    }, [visibleTabs.length, selectedTabIndex]);

    const detailsTabIndex = visibleTabs.findIndex((tab) => tab.label === 'detalles');

    useEffect(() => {
        if (!isOpen) return;
        const isDetails = detailsTabIndex !== -1 && selectedTabIndex === detailsTabIndex;
        onDetailsTabActiveChange?.(isDetails);
    }, [selectedTabIndex, detailsTabIndex, onDetailsTabActiveChange, isOpen]);

    const handleReceiptEditModeChange = useCallback(
        (editing) => {
            setIsEditingReceiptDate(editing);
            if (onReceiptEditModeChange) {
                onReceiptEditModeChange(editing);
            }
        },
        [onReceiptEditModeChange]
    );

    const handleReceiptCancelCallbackRegister = useCallback(
        (callback) => {
            cancelReceiptEditRef.current = () => {
                if (!callback) return;
                const maybeHandler = callback();
                if (typeof maybeHandler === 'function') {
                    maybeHandler();
                }
            };
            if (onReceiptDateCancelCallbackChange) {
                onReceiptDateCancelCallbackChange(callback);
            }
        },
        [onReceiptDateCancelCallbackChange]
    );

    const handleTabChange = (nextTabIndex) => {
        if (
            detailsTabIndex !== -1 &&
            selectedTabIndex === detailsTabIndex &&
            nextTabIndex !== detailsTabIndex
        ) {
            onClearReceiptAux?.();
        }
        if (
            detailsTabIndex !== -1 &&
            selectedTabIndex === detailsTabIndex &&
            nextTabIndex !== detailsTabIndex &&
            isEditingReceiptDate
        ) {
            cancelReceiptEditRef.current?.();
            setIsEditingReceiptDate(false);
            if (onReceiptEditModeChange) {
                onReceiptEditModeChange(false);
            }
        }
        setSelectedTabIndex(nextTabIndex);
    };

    return (
        <div className="w-full h-full flex flex-col">
            <Tab.Group selectedIndex={selectedTabIndex} onChange={handleTabChange}>
                {() => (
                    <div className="h-full flex flex-col">
                        {/* Scroll horizontal en móvil, barra de scroll oculta */}
                        <div className="flex-shrink-0 border-b border-gray-200 bg-white overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
                            <Tab.List className="flex border-b-0 min-w-max md:min-w-0 md:w-full">
                                {visibleTabs.map((tab) => {
                                    const { label, icon } = tab;
                                    return (
                                        <Tab
                                            key={label}
                                            className={({ selected }) =>
                                                classNames(
                                                    'group relative flex items-center justify-center gap-2 flex-shrink-0 md:flex-1 px-4 py-3.5 text-sm font-semibold transition-all duration-200 focus:outline-none',
                                                    selected
                                                        ? 'text-indigo-600'
                                                        : 'text-gray-600 hover:text-indigo-500 hover:bg-gray-50'
                                                )
                                            }
                                        >
                                            {({ selected }) => (
                                                <>
                                                    <span className={classNames(
                                                        'transition-all duration-200',
                                                        selected ? 'scale-110' : 'group-hover:scale-105'
                                                    )}>
                                                        {icon}
                                                    </span>
                                                    <span className="capitalize tracking-wide whitespace-nowrap">
                                                        {label}
                                                    </span>
                                                    {/* Indicador inferior animado */}
                                                    {selected && (
                                                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-indigo-500/50" />
                                                    )}
                                                </>
                                            )}
                                        </Tab>
                                    )
                                }
                                )}
                            </Tab.List>
                        </div>
                        <Tab.Panels className="bg-white flex-1 overflow-auto">
                            {visibleTabs.map((tab, index) => {
                                const { label } = tab;
                                return (
                                    <Tab.Panel
                                        key={index}
                                        className={classNames(
                                            'p-6 focus:outline-none h-full',
                                            'transition-all duration-300 ease-in-out'
                                        )}
                                    >
                                        {label === 'detalles' && (
                                            <TabPaymentDetails
                                                idPayment={idPayment}
                                                toggleDrawerFilters={toggleDrawerFilters}
                                                updateDataTable={updateDataTable}
                                                onValidatePayment={onValidatePayment}
                                                onDeclinePayment={onDeclinePayment}
                                                onLoadingChange={onLoadingChange}
                                                isOpen={isOpen}
                                                onEditModeChange={handleReceiptEditModeChange}
                                                onSaveCallbackChange={onReceiptDateSaveCallbackChange}
                                                onCancelCallbackChange={handleReceiptCancelCallbackRegister}
                                                onLoadingSavingChange={onReceiptDateLoadingSavingChange}
                                                loadingReceiptDateSaving={loadingReceiptDateSaving}
                                                isReceiptAuxOpen={isReceiptAuxOpen}
                                            />
                                        )}
                                        {label === 'pagos' && <TabPaymentHistory />}
                                        {label === 'cursos' && <TabCoursesHistory />}
                                    </Tab.Panel>
                                )
                            })}
                        </Tab.Panels>
                    </div>
                )}
            </Tab.Group>
        </div>
    )
}